import React from 'react'
import { AlertTriangle, BarChart3, RefreshCw } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { useProspectingUsage } from '../hooks/useProspectingUsage'
import type { ProspectingActivity, ProspectingRepUsage, ProspectingUsageMeters, UsageMeter, WorkspaceRole } from '../types'

type Props = {
  workspaceId: string
}

// Rendered in this order; a resource the API adds later (e.g. vendor credit
// spend) only needs a label here.
const METER_LABELS: Record<keyof ProspectingUsageMeters, string> = {
  searches: 'Web searches',
  modelCalls: 'Model calls',
  tokens: 'Model tokens',
}
const METER_KEYS = Object.keys(METER_LABELS) as Array<keyof ProspectingUsageMeters>

// Same threshold as the near-cap admin notification (ZAI-392).
const WARN_RATIO = 0.8

const ROLE_LABELS: Record<WorkspaceRole, string> = { owner: 'Owner', admin: 'Admin', member: 'Member' }

const fmt = (n: number) => n.toLocaleString()

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const MeterBar: React.FC<{ label: string; meter: UsageMeter }> = ({ label, meter }) => {
  const { currentTheme: t } = useTheme()
  const ratio = meter.cap ? Math.min(meter.used / meter.cap, 1) : 0
  const fill = ratio >= 1 ? t.errorText : ratio >= WARN_RATIO ? t.warningText : t.accent
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2 text-[13px]">
        <span style={{ color: t.secondrytext }}>{label}</span>
        <span className="font-medium tabular-nums" style={{ color: t.text }}>
          {fmt(meter.used)}
          {meter.cap !== null && <span style={{ color: t.mutedText }}> / {fmt(meter.cap)}</span>}
        </span>
      </div>
      {meter.cap !== null && (
        <div
          className="h-1.5 rounded-full overflow-hidden"
          style={{ backgroundColor: t.hoverBackground }}
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={meter.cap}
          aria-valuenow={meter.used}
        >
          <div className="h-full rounded-full transition-all" style={{ width: `${ratio * 100}%`, backgroundColor: fill }} />
        </div>
      )}
    </div>
  )
}

const StatTile: React.FC<{ label: string; value: number; detail?: string }> = ({ label, value, detail }) => {
  const { currentTheme: t } = useTheme()
  return (
    <div className="rounded-xl p-4 flex flex-col gap-1" style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}`, boxShadow: t.cardShadow }}>
      <span className="text-[12px] font-medium" style={{ color: t.secondrytext }}>{label}</span>
      <span className="text-2xl font-semibold tabular-nums" style={{ color: t.text }}>{fmt(value)}</span>
      {detail && <span className="text-[12px]" style={{ color: t.mutedText }}>{detail}</span>}
    </div>
  )
}

function runDetail(a: ProspectingActivity): string {
  const parts = [`${fmt(a.runs.completed)} completed`, `${fmt(a.runs.failed)} failed`]
  if (a.runs.inProgress > 0) parts.push(`${fmt(a.runs.inProgress)} running`)
  return parts.join(' · ')
}

function dedupeDetail(a: ProspectingActivity): string {
  return `${fmt(a.deduped.existingCandidate)} seen before · ${fmt(a.deduped.crmCompany + a.deduped.crmContact)} in HubSpot`
}

function repName(rep: ProspectingRepUsage): string {
  return rep.name ?? 'Former member'
}

// Columns shared by the desktop table and the mobile cards.
const REP_COLUMNS: Array<{ label: string; value: (r: ProspectingRepUsage) => number }> = [
  { label: 'Runs', value: (r) => r.runs.total },
  { label: 'Found', value: (r) => r.candidates.found },
  { label: 'Accepted', value: (r) => r.candidates.accepted },
  { label: 'Rejected', value: (r) => r.candidates.rejected },
  { label: 'Deduped', value: (r) => r.deduped.total },
  { label: 'Searches', value: (r) => r.meters.searches.used },
  { label: 'Model calls', value: (r) => r.meters.modelCalls.used },
]

const RepTable: React.FC<{ reps: ProspectingRepUsage[] }> = ({ reps }) => {
  const { currentTheme: t } = useTheme()
  return (
    <>
      <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}`, boxShadow: t.cardShadow }}>
        <table className="w-full text-[13px]">
          <thead>
            <tr style={{ backgroundColor: t.sectionBackground, color: t.secondrytext }}>
              <th scope="col" className="text-left font-medium px-4 py-2.5">Rep</th>
              {REP_COLUMNS.map((c) => (
                <th key={c.label} scope="col" className="text-right font-medium px-3 py-2.5 whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reps.map((rep) => (
              <tr key={rep.userId} style={{ borderTop: `1px solid ${t.border}` }}>
                <td className="px-4 py-2.5">
                  <div className="font-medium" style={{ color: rep.name ? t.text : t.mutedText }}>{repName(rep)}</div>
                  {rep.role && <div className="text-[12px]" style={{ color: t.mutedText }}>{ROLE_LABELS[rep.role]}</div>}
                </td>
                {REP_COLUMNS.map((c) => {
                  const v = c.value(rep)
                  return (
                    <td key={c.label} className="text-right px-3 py-2.5 tabular-nums" style={{ color: v === 0 ? t.mutedText : t.text }}>{fmt(v)}</td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden flex flex-col gap-3">
        {reps.map((rep) => (
          <div key={rep.userId} className="rounded-xl p-4" style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}`, boxShadow: t.cardShadow }}>
            <div className="flex items-baseline justify-between gap-2 mb-3">
              <span className="font-medium text-sm" style={{ color: rep.name ? t.text : t.mutedText }}>{repName(rep)}</span>
              {rep.role && <span className="text-[12px]" style={{ color: t.mutedText }}>{ROLE_LABELS[rep.role]}</span>}
            </div>
            <dl className="grid grid-cols-3 gap-x-3 gap-y-2 text-[12px]">
              {REP_COLUMNS.map((c) => (
                <div key={c.label}>
                  <dt style={{ color: t.secondrytext }}>{c.label}</dt>
                  <dd className="font-medium tabular-nums text-[13px]" style={{ color: t.text }}>{fmt(c.value(rep))}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </>
  )
}

/** Owner/Admin only — the caller must not render it for members. */
const ProspectingUsageSection: React.FC<Props> = ({ workspaceId }) => {
  const { currentTheme: t } = useTheme()
  const { usage, loading, error, reload } = useProspectingUsage(workspaceId)

  if (loading && !usage) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-24 rounded-xl animate-pulse" style={{ backgroundColor: t.hoverBackground }} />)}
        </div>
        <div className="h-28 rounded-xl animate-pulse" style={{ backgroundColor: t.hoverBackground }} />
        <div className="h-48 rounded-xl animate-pulse" style={{ backgroundColor: t.hoverBackground }} />
      </div>
    )
  }

  if (error || !usage) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl" style={{ backgroundColor: t.errorBackground }}>
          <AlertTriangle size={24} style={{ color: t.errorText }} />
        </div>
        <div>
          <p className="font-semibold text-base" style={{ color: t.text }}>Couldn&apos;t load usage</p>
          <p className="text-sm mt-1" style={{ color: t.mutedText }}>{error ?? 'Something went wrong'}</p>
        </div>
        <button
          type="button"
          onClick={reload}
          className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium"
          style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
        >
          <RefreshCw size={14} />
          Retry
        </button>
      </div>
    )
  }

  const ws = usage.workspace
  const activeReps = usage.reps.filter((r) => r.runs.total > 0 || r.candidates.found > 0)

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[13px]" style={{ color: t.secondrytext }}>
          {formatDate(usage.period.start)} – {formatDate(usage.period.end)} · resets {formatDate(usage.period.end)}
        </p>
        <button
          type="button"
          onClick={reload}
          disabled={loading}
          className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-[13px] font-medium disabled:opacity-60"
          style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : undefined} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile label="Runs" value={ws.runs.total} detail={runDetail(ws)} />
        <StatTile label="Candidates found" value={ws.candidates.found} detail={`${fmt(ws.candidates.pending)} awaiting review`} />
        <StatTile label="Accepted / rejected" value={ws.candidates.accepted} detail={`${fmt(ws.candidates.rejected)} rejected`} />
        <StatTile label="Deduped" value={ws.deduped.total} detail={dedupeDetail(ws)} />
      </div>

      <section className="rounded-xl p-4 flex flex-col gap-4" style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}`, boxShadow: t.cardShadow }}>
        <h2 className="text-sm font-semibold" style={{ color: t.text }}>Workspace usage this period</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {METER_KEYS.map((key) => <MeterBar key={key} label={METER_LABELS[key]} meter={ws.meters[key]} />)}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold" style={{ color: t.text }}>By rep</h2>
        {activeReps.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-center rounded-xl" style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}` }}>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: t.hoverBackground }}>
              <BarChart3 size={22} style={{ color: t.mutedText }} />
            </div>
            <div className="max-w-sm">
              <p className="font-semibold text-sm" style={{ color: t.text }}>No prospecting yet this period</p>
              <p className="text-[13px] mt-1" style={{ color: t.mutedText }}>When your team runs Find prospects, each rep&apos;s activity shows up here.</p>
            </div>
          </div>
        ) : (
          <RepTable reps={usage.reps} />
        )}
      </section>
    </div>
  )
}

export default ProspectingUsageSection
